/**
 * Auth Service - Multi-Session JWT Authentication
 * Handles access token management, refresh flow, and device identification
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Storage keys
const DEVICE_ID_KEY = 'pservice_device_id';
const ACCESS_TOKEN_KEY = 'pservice_access_token'; // Will be stored in memory, not localStorage

// In-memory token storage (more secure than localStorage)
let accessToken = null;
let tokenExpiry = null;
let refreshPromise = null;

/**
 * Generate or retrieve device ID
 */
export function getDeviceId() {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = `dev_${Math.random().toString(36).substring(2, 15)}${Date.now().toString(36)}`;
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
}

/**
 * Set access token in memory
 */
export function setAccessToken(token, expiresIn = 900) {
  accessToken = token;
  tokenExpiry = Date.now() + (expiresIn * 1000) - 30000; // Refresh 30s before expiry
}

/**
 * Get current access token
 */
export function getAccessToken() {
  return accessToken;
}

/**
 * Clear tokens (on logout)
 */
export function clearTokens() {
  accessToken = null;
  tokenExpiry = null;
  refreshPromise = null;
}

/**
 * Check if token needs refresh
 */
export function isTokenExpired() {
  if (!accessToken || !tokenExpiry) return true;
  return Date.now() >= tokenExpiry;
}

/**
 * Refresh access token
 */
export async function refreshAccessToken() {
  // Prevent multiple concurrent refresh requests
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          device_id: getDeviceId()
        })
      });

      if (!response.ok) {
        clearTokens();
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      setAccessToken(data.access_token, data.expires_in);
      return data.access_token;
    } catch (error) {
      clearTokens();
      throw error;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Get valid access token (refresh if needed)
 */
export async function getValidAccessToken() {
  if (!isTokenExpired()) {
    return accessToken;
  }
  return refreshAccessToken();
}

/**
 * Authenticated fetch wrapper
 */
export async function authFetch(url, options = {}) {
  // Get valid token
  let token;
  try {
    token = await getValidAccessToken();
  } catch (error) {
    // Token refresh failed, proceed without token (will use cookie)
    token = null;
  }

  // Add auth header if we have a token
  const headers = {
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include'
  });

  // If unauthorized and we had a token, try refreshing once
  if (response.status === 401 && token) {
    try {
      const newToken = await refreshAccessToken();
      headers['Authorization'] = `Bearer ${newToken}`;
      return fetch(url, {
        ...options,
        headers,
        credentials: 'include'
      });
    } catch (error) {
      console.warn('Token refresh after 401 failed, returning original response:', error);
    }
  }

  return response;
}

/**
 * Login and store tokens
 */
export async function login(username, password) {
  const response = await fetch(`${API}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({
      username,
      password,
      device_id: getDeviceId()
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Login failed');
  }

  const data = await response.json();
  
  // Store access token in memory
  if (data.access_token) {
    setAccessToken(data.access_token, data.expires_in);
  }

  return data;
}

/**
 * Logout (clears current session only)
 */
export async function logout() {
  try {
    await authFetch(`${API}/auth/logout`, {
      method: 'POST'
    });
  } catch (error) {
    console.warn('Logout request failed (clearing tokens anyway):', error);
  }
  clearTokens();
}

/**
 * Get current user
 */
export async function getCurrentUser() {
  const response = await authFetch(`${API}/auth/me`);
  if (!response.ok) {
    throw new Error('Not authenticated');
  }
  return response.json();
}

/**
 * Get active sessions
 */
export async function getSessions() {
  const response = await authFetch(`${API}/auth/sessions`);
  if (!response.ok) {
    throw new Error('Failed to get sessions');
  }
  return response.json();
}

/**
 * Revoke a specific session
 */
export async function revokeSession(sessionId) {
  const response = await authFetch(`${API}/auth/sessions/${sessionId}`, {
    method: 'DELETE'
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to revoke session');
  }
  return response.json();
}

/**
 * Revoke all other sessions
 */
export async function revokeAllOtherSessions() {
  const response = await authFetch(`${API}/auth/sessions`, {
    method: 'DELETE'
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to revoke sessions');
  }
  return response.json();
}

/**
 * Setup automatic token refresh
 */
let refreshInterval = null;

export function startAutoRefresh() {
  // Clear any existing interval
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }

  // Check every minute if token needs refresh
  refreshInterval = setInterval(async () => {
    if (accessToken && isTokenExpired()) {
      try {
        await refreshAccessToken();
      } catch (error) {
        console.warn('Auto-refresh failed:', error);
      }
    }
  }, 60000);
}

export function stopAutoRefresh() {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
}

// Export default object for convenience
export default {
  getDeviceId,
  setAccessToken,
  getAccessToken,
  clearTokens,
  isTokenExpired,
  refreshAccessToken,
  getValidAccessToken,
  authFetch,
  login,
  logout,
  getCurrentUser,
  getSessions,
  revokeSession,
  revokeAllOtherSessions,
  startAutoRefresh,
  stopAutoRefresh
};
